import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useStoreState } from 'easy-peasy';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { colors } from '../../constant';
import Box from '../../common/box/Box';
import Text from '../../common/text/Text';
import Icon from '../../common/icon/Icon';
import Title from '../../common/title/Title';
import Button from '../../common/button/Button';
import hasPermission from '../../../security/Permission';
import { mobile } from '../../../constants/breakpoints';

import { ButtonWrapper } from './BucketObjects';
import { readSubAccountStatus } from '../endpoints';

export function EmptyObjectsView({
  onUploadFileClick,
  onCreateFolderClick,
  incorrectFileName,
  breadCrumbsLength
}) {
  const { t } = useTranslation();
  const canCreateFilePerm = hasPermission('oem_objects', 'create_file');
  const canCreateFolderPerm = hasPermission('oem_objects', 'create_folder');
  const { filesUploadingGlobal } = useStoreState(actions => actions.fileUpload);
  const isMobile = useMediaQuery({ query: `(max-width: ${mobile}px)` });
  const filesUploading = Boolean(filesUploadingGlobal?.length > 0);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const { getRootProps, isDragActive } = useDropzone({
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: true,
    disabled: filesUploading,
    onDrop: acceptedFiles => {
      if (canCreateFilePerm) {
        acceptedFiles.map((file, index) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file)
          })
        );
        const files = [];
        let filePaths = [];
        acceptedFiles.forEach(acceptedFile => {
          const fileName = acceptedFile.name;
          const filePath = acceptedFile.path.replace(fileName, '');

          if (
            ['%', '<', '>'].some(
              item => acceptedFile.path.indexOf(item) > -1
            ) ||
            fileName.indexOf('%') > -1
          ) {
            incorrectFileName();
          } else {
            files.push({
              file: acceptedFile,
              percentage: 0,
              displaySuccessIcon: false,
              displayErrorIcon: false
            });

            let previousPath = '/';
            filePath
              .split('/')
              .filter(e => e !== '')
              .forEach(function(item) {
                const path = previousPath + item + '/';
                filePaths.push(path);
                previousPath = path;
              });
          }
        });
        filePaths = [...new Set(filePaths)];
        filePaths.forEach(function(path) {
          // This makes sure to not add default path "/./" as a folder and already uploaded files are not replaced
          if (
            path !== '/./' &&
            !files.some(item => item?.file?.name === path)
          ) {
            files.push({
              file: new File([''], path, {}),
              percentage: 0,
              displaySuccessIcon: false,
              displayErrorIcon: false
            });
          }
        });

        onUploadFileClick(files);
      }
    }
  });

  /**
   * Upload Files.
   */
  const showUploadFilesModal = () => {
    onUploadFileClick();
  };

  /**
   * Create folder.
   */
  const showCreateFolderModal = () => {
    onCreateFolderClick();
  };

  useEffect(() => {
    readSubAccountStatus().then(([res]) => {
      const subAccountStatus = res?.data?.data;
      if (subAccountStatus === 'QUOTA_PAUSE') {
        setButtonsDisabled(true);
      } else {
        setButtonsDisabled(false);
      }
    });
  }, []);

  return (
    <Box>
      {canCreateFilePerm && (
        <Box maxwidth={isMobile ? '530px' : '540px'} m="0 auto">
          <Box
            {...getRootProps({
              className: `${
                isMobile ? 'mobile-dropzone-width' : 'dropzone-width'
              } dropzone dropzone-empty-view ${
                isDragActive ? 'dragActive' : ''
              }`
            })}
          >
            <UploadIcon icon="upload" mb="10px" />

            <Title fontSize="20px" color={colors.black} fontWeight="500">
              {t('your')}{' '}
              {breadCrumbsLength > 2 ? t('folder') : t('bucketLowercase')}{' '}
              {t('isEmpty')}
            </Title>

            <Text
              m="10px 0 30px"
              fontSize="14px"
              color={colors.bodyColor}
              data-react-if={canCreateFilePerm}
            >
              {t('dragOrClickToUpload')}
            </Text>

            <ButtonWrapper>
              <Button
                disabled={buttonsDisabled}
                primary
                minHeight="40px"
                onClick={showUploadFilesModal}
                m={isMobile ? 0 : '0 30px'}
                data-react-if={canCreateFilePerm}
              >
                {t('uploadFiles')}
              </Button>
              <Button
                disabled={buttonsDisabled}
                primary
                minHeight="40px"
                onClick={showCreateFolderModal}
                data-react-if={canCreateFolderPerm}
              >
                {t('createFolder')}
              </Button>
            </ButtonWrapper>
          </Box>
        </Box>
      )}
    </Box>
  );
}

const UploadIcon = styled(Icon)`
  cursor: default;
`;

EmptyObjectsView.propTypes = {
  onUploadFileClick: PropTypes.func.isRequired,
  onCreateFolderClick: PropTypes.func.isRequired,
  incorrectFileName: PropTypes.bool.isRequired,
  breadCrumbsLength: PropTypes.number.isRequired
};
